import Navigation from '@/components/Navigation'
import CTASection from '@/components/CTASection'
import Footer from '@/components/Footer'
import { LightboxProvider, LightboxImage } from '@/components/Lightbox'
import { Link } from 'wouter'
import { usePageMeta } from '@/hooks/usePageMeta'

const TellMeMorePage = () => {
  usePageMeta({
    title: 'Tell Me More | SPOKE Film',
    description: 'Get involved. Learn the full story behind SPOKE — the firefighter tribute documentary — and become part of the movement.',
  })
  return (
    <LightboxProvider>
      <Navigation />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center justify-center pt-16 px-6 overflow-hidden">
        <img
          src="/spoke-static/images/SPOKE_firefighters_stairwell_headlights.jpg"
          alt="SPOKE film firefighters descending stairwell with helmet lights"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/20"></div>
        <div className="relative z-10 max-w-5xl mx-auto w-full text-center">
          <h1 className="text-5xl md:text-7xl font-montserrat font-black uppercase tracking-wide text-yellow-400 leading-none">
            Become Part<br className="hidden md:block" /> of the Story
          </h1>
        </div>
      </section>

      <CTASection />

      {/* Don't Miss Your Chance */}
      <section className="py-20 px-6 bg-[#0a0f1a]">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-12 h-1 bg-red-600 mx-auto mb-10"></div>
          <h2 className="text-4xl md:text-5xl font-montserrat font-black uppercase tracking-wide text-white mb-4">
            DON'T MISS YOUR CHANCE
          </h2>
          <p className="text-gray-400 text-lg font-poppins font-light mb-10">
            To make a difference.
          </p>
          <Link
            href="/get-in-touch"
            className="inline-block font-montserrat font-black uppercase tracking-widest text-xl px-12 py-4 bg-yellow-400 text-black hover:bg-yellow-300 transition-colors duration-300"
          >
            GET IN TOUCH
          </Link>
        </div>
      </section>

      {/* Cast & Experts */}
      <section id="cast" className="py-20 bg-black">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="flex flex-col items-end text-right mb-12">
            <div className="w-12 h-1 bg-yellow-400 mb-6"></div>
            <h2 className="text-4xl md:text-5xl font-montserrat font-black uppercase tracking-wide text-white mb-4">
              Cast &amp; Experts
            </h2>
            <p className="text-gray-400 font-poppins font-light text-lg max-w-2xl">
              Real firefighters in front of and behind the camera — and the specialists who shaped every detail of this film.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-16">
            <LightboxImage src="/spoke-static/images/cast-still-4.jpg" alt="Firefighter on set" wrapperClassName="aspect-[3/4]" className="w-full h-full object-cover" />
            <LightboxImage src="/spoke-static/images/cast-still-1.jpg" alt="Cast still" wrapperClassName="aspect-[3/4]" className="w-full h-full object-cover" />
            <LightboxImage src="/spoke-static/images/cast-still-2.jpg" alt="Cast still" wrapperClassName="aspect-[3/4]" className="w-full h-full object-cover" />
            <LightboxImage src="/spoke-static/images/cast-still-3.jpg" alt="Cast still" wrapperClassName="aspect-[3/4]" className="w-full h-full object-cover" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <div className="flex items-center gap-3 mb-6"><div className="w-8 h-[2px] bg-red-600"></div><h3 className="font-montserrat font-black uppercase tracking-widest text-white text-sm">Firehouse 13 Cast</h3></div>
              <ul className="font-poppins text-gray-300 space-y-2 text-base mb-8">
                <li>Lieutenant Steven Trujillo</li><li>Cody Olson</li><li>Jace Reyes</li><li>Danny Nguyen</li><li>Lance Lawson</li>
              </ul>
              <div className="flex items-center gap-3 mb-6"><div className="w-8 h-[2px] bg-red-600"></div><h3 className="font-montserrat font-black uppercase tracking-widest text-white text-sm">The Pospisil Family</h3></div>
              <ul className="font-poppins text-gray-300 space-y-2 text-base mb-8">
                <li>Fire Captain Rudy Pospisil</li><li>Susan Pospisil</li><li>Eric Pospisil</li><li>Adam Pospisil</li><li>Kelly Pospisil</li>
              </ul>
              <div className="flex items-center gap-3 mb-6"><div className="w-8 h-[2px] bg-red-600"></div><h3 className="font-montserrat font-black uppercase tracking-widest text-white text-sm">Additional Cast</h3></div>
              <ul className="font-poppins text-gray-300 space-y-2 text-base">
                <li>Deputy Chief Josh Kelch</li><li>Deputy Chief Mark Seaver</li><li>Chaplain Loretta Green</li><li>Firefighter Gregory Kelley</li>
              </ul>
            </div>
            <div>
              <div className="flex items-center gap-3 mb-6"><div className="w-8 h-[2px] bg-yellow-400"></div><h3 className="font-montserrat font-black uppercase tracking-widest text-white text-sm">Expert Consultants</h3></div>
              <ul className="font-poppins text-gray-300 space-y-6 text-base">
                <li><span className="text-white font-semibold">Jacqueline Toomey, BA</span><span className="block text-gray-400 text-sm mt-1">Somatic Sleep Specialist · Co-Creator, First Responder Sleep Recovery Program</span></li>
                <li><span className="text-white font-semibold">Morgan Jarman, MS, RD, CSSD, CEP</span><span className="block text-gray-400 text-sm mt-1">Director of Performance Nutrition, University of Washington Athletics</span></li>
                <li><span className="text-white font-semibold">Christopher Robbins</span><span className="block text-gray-400 text-sm mt-1">Chief Technology Officer, Tersus Solutions · CO₂ Textile and PPE Decontamination Technology Lead</span></li>
                <li><span className="text-white font-semibold">Firefighter Kiel Hicks</span><span className="block text-gray-400 text-sm mt-1">Additional Cinematography and Editing</span></li>
                <li><span className="text-white font-semibold">Firefighter Gregory Kelley</span><span className="block text-gray-400 text-sm mt-1">Additional Cinematography and Marketing Lead</span></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Director's Statement */}
      <section className="pt-8 pb-20 bg-[#0a0f1a] px-6">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-[2px] bg-red-600"></div>
            <h2 className="font-montserrat font-black uppercase tracking-widest text-white text-sm">Director's Statement</h2>
          </div>
          <div className="space-y-6 font-poppins font-light text-gray-300 text-base md:text-lg leading-relaxed">
            <p>SPOKE was born in two acts.</p>
            <p>The first began in 2023, when I met retired Canadian Fire Captain Rudy Pospisil. Drawing upon my Bulgarian culture of gathering around the table to tell the stories we carry, our team sat Rudy and his family down on a sound stage, placed two cameras on a dolly, and let them circle. Each take ran about forty minutes. Slowly, Rudy began to open up to his family and to himself. His children, all grown now, were hearing some of these harrowing stories from the line of duty for the very first time.</p>
            <p>The second act came in 2025, after a dream. I was following firefighters on duty as they pulled a man out of quicksand. I woke up knowing that this was the image at the heart of SPOKE: Firefighters literally sinking under the weight of what the job demands. I called one of our producers and said, "We're not done filming yet." That dream led us to Firehouse 13 and to the young US crew who showed me the other side of this story.</p>
            <p>What emerged was a film where no single voice sits at the center. Rudy and the crew of Firehouse 13, Canada and the US, the veterans and the young, each at an equal distance from the middle, doing their part.</p>
          </div>
          <p className="mt-8 font-montserrat font-black uppercase tracking-widest text-yellow-400 text-sm">— Bogdan Darev</p>
        </div>
      </section>

      <Footer />
    </LightboxProvider>
  )
}

export default TellMeMorePage
